import { useState, useRef, useCallback, useEffect, RefObject } from 'react';
import { BLUR_DELAY_MS, TOAST_DURATIONS } from '../constants/reactionScheme';

interface UseCharacterPaletteProps {
  reagentsInputRef: RefObject<HTMLInputElement | null>;
  conditionsInputRef: RefObject<HTMLInputElement | null>;
  topText: string;
  bottomText: string;
  setTopText: (text: string) => void;
  setBottomText: (text: string) => void;
  showToastMessage: (message: string, duration?: number) => void;
}

export function useCharacterPalette({
  reagentsInputRef,
  conditionsInputRef,
  topText,
  bottomText,
  setTopText,
  setBottomText,
  showToastMessage,
}: UseCharacterPaletteProps) {
  const [activeInput, setActiveInput] = useState<'reagents' | 'conditions' | null>(null);
  const [copiedCharacter, setCopiedCharacter] = useState<string | null>(null);
  const blurTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup blur timeout on unmount
  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
    };
  }, []);

  const handleCharacterSelect = useCallback((char: string) => {
    // Cancel any pending blur timeout when clicking a character
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }

    const inputRef = activeInput === 'reagents' ? reagentsInputRef : conditionsInputRef;

    if (!inputRef.current) {
      return;
    } else if (!activeInput) {
      // Copy the character to the clipboard
      navigator.clipboard.writeText(char).then(() => {
        setCopiedCharacter(char);
        showToastMessage('Copied', TOAST_DURATIONS.SHORT);

        // Clear copiedCharacter after toast
        setTimeout(() => {
          setCopiedCharacter(null);
        }, TOAST_DURATIONS.SHORT);
      });
      return;
    }

    const input = inputRef.current;
    const start = input.selectionStart ?? 0;
    const end = input.selectionEnd ?? 0;
    const currentValue = activeInput === 'reagents' ? topText : bottomText;
    const newValue = currentValue.slice(0, start) + char + currentValue.slice(end);

    if (activeInput === 'reagents') {
      setTopText(newValue);
    } else {
      setBottomText(newValue);
    }

    // Restore cursor position after React re-render
    requestAnimationFrame(() => {
      input.focus();
      input.setSelectionRange(start + char.length, start + char.length);
    });
  }, [
    activeInput,
    reagentsInputRef,
    conditionsInputRef,
    topText,
    bottomText,
    setTopText,
    setBottomText,
    showToastMessage,
  ]);

  const handleInputFocus = useCallback((inputType: 'reagents' | 'conditions') => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    setActiveInput(inputType);
  }, []);

  const handleInputBlur = useCallback(() => {
    // Delay hiding the palette to allow clicks on character buttons to register
    blurTimeoutRef.current = setTimeout(() => {
      setActiveInput(null);
    }, BLUR_DELAY_MS);
  }, []);

  return {
    activeInput,
    copiedCharacter,
    handleCharacterSelect,
    handleInputFocus,
    handleInputBlur,
  };
}